import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Check, ShieldCheck } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { FadeUp, Overline } from "../components/Motion";
import { SERVICE_OPTIONS, PACKAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", preferred_date: "", message: "" };

const Field = ({ label, children, testid }) => (
  <label className="block group" data-testid={testid}>
    <span className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] group-focus-within:text-[#F5A623] transition-colors duration-300">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-white/20 py-3 text-lg text-white placeholder-[#52525B] focus:border-[#F5A623] focus:outline-none transition-colors duration-300";

const Booking = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | pay | done
  const [booking, setBooking] = useState(null);
  const [pp, setPp] = useState(null); // paypal config

  useEffect(() => {
    axios.get(`${API}/config/paypal`).then((r) => setPp(r.data)).catch(() => setPp({ configured: false }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      setBooking(data);
      if (pp && pp.configured) {
        setStep("pay");
        toast.success("Booking saved — secure your session with a deposit below.");
      } else {
        setStep("done");
        toast.success("Request received — we'll be in touch within 24 hours.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="bg-[#0A0A0A] py-28 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <Overline>Book a Session</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.02]">
              Let's make <span className="italic">something</span> worth framing.
            </h2>
            <p className="text-[#A1A1AA] mt-8 leading-relaxed max-w-md">
              Tell us about your shoot. A <span className="text-white">$50 deposit</span> secures
              your date and is applied to your final package price.
            </p>

            <div className="mt-10 border-t border-white/10 pt-8 space-y-4" data-testid="package-pricing">
              {PACKAGES.map((p) => (
                <div key={p.name} className="flex items-baseline justify-between gap-4">
                  <div>
                    <span className="text-white">{p.name}</span>
                    {p.note && <span className="block text-xs text-[#52525B] uppercase tracking-wider">{p.note}</span>}
                  </div>
                  <span className="font-serif text-2xl text-[#F5A623]">${p.price}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.1}>
            {step === "form" && (
              <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Full Name" testid="field-name">
                  <input data-testid="input-name" className={inputCls} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                </Field>
                <Field label="Email" testid="field-email">
                  <input data-testid="input-email" type="email" className={inputCls} placeholder="jane@company.com" value={form.email} onChange={set("email")} />
                </Field>
                <Field label="Phone" testid="field-phone">
                  <input data-testid="input-phone" className={inputCls} placeholder="(210) 000-0000" value={form.phone} onChange={set("phone")} />
                </Field>
                <Field label="Preferred Date" testid="field-date">
                  <input data-testid="input-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.preferred_date} onChange={set("preferred_date")} />
                </Field>

                <div className="md:col-span-2">
                  <Field label="Service" testid="field-service">
                    <div className="flex flex-wrap gap-3 mt-3">
                      {SERVICE_OPTIONS.map((opt) => (
                        <button
                          type="button"
                          key={opt}
                          data-testid={`service-opt-${opt}`}
                          onClick={() => setForm((f) => ({ ...f, service: opt }))}
                          className={`text-sm rounded-full px-5 py-2.5 border transition-colors duration-300 ${
                            form.service === opt
                              ? "bg-white text-black border-white"
                              : "border-white/20 text-[#A1A1AA] hover:border-white/60 hover:text-white"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="Project Details" testid="field-message">
                    <textarea data-testid="input-message" rows={3} className={`${inputCls} resize-none`} placeholder="Tell us about the shoot, location, headcount..." value={form.message} onChange={set("message")} />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <motion.button
                    data-testid="submit-booking-btn"
                    type="submit"
                    disabled={loading}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-white text-black font-semibold text-lg rounded-full py-5 flex items-center justify-center gap-3 hover:bg-[#F5A623] disabled:opacity-70 transition-colors duration-300"
                  >
                    {loading ? (
                      <><Loader2 className="animate-spin" size={20} /> Sending...</>
                    ) : pp && pp.configured ? (
                      "Continue to Deposit →"
                    ) : (
                      "Send Booking Request"
                    )}
                  </motion.button>
                </div>
              </form>
            )}

            {step === "pay" && booking && (
              <div data-testid="payment-step">
                <div className="border border-white/10 rounded-lg p-8 bg-[#050505]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Booking Deposit</span>
                    <span className="font-serif text-3xl text-white">${pp.deposit}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm mb-6">
                    {booking.service} · {booking.name}. This deposit confirms your session and is credited toward your package.
                  </p>
                  <PayPalScriptProvider options={{ clientId: pp.client_id, currency: "USD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                      createOrder={async () => {
                        const { data } = await axios.post(`${API}/payments/create-order`, { booking_id: booking.id });
                        return data.order_id;
                      }}
                      onApprove={async (data) => {
                        try {
                          const { data: res } = await axios.post(`${API}/payments/capture-order/${data.orderID}`);
                          if (res.paid) {
                            setStep("done");
                            toast.success("Deposit received — your session is confirmed!");
                          } else {
                            toast.error("Payment was not completed. Please try again.");
                          }
                        } catch {
                          toast.error("Could not confirm payment. Please contact us.");
                        }
                      }}
                      onError={() => toast.error("PayPal error. Please try again.")}
                    />
                  </PayPalScriptProvider>
                  <button
                    data-testid="skip-deposit-btn"
                    onClick={() => setStep("done")}
                    className="mt-4 text-sm text-[#52525B] hover:text-white transition-colors duration-300 underline underline-offset-4"
                  >
                    I'll pay the deposit later
                  </button>
                </div>
              </div>
            )}

            {step === "done" && (
              <div data-testid="booking-done" className="border border-white/10 rounded-lg p-10 bg-[#050505] flex flex-col items-start gap-4">
                <span className="h-14 w-14 rounded-full bg-[#F5A623] text-black flex items-center justify-center">
                  <Check size={28} />
                </span>
                <h3 className="font-serif text-3xl">Request received.</h3>
                <p className="text-[#A1A1AA]">
                  Thank you{booking ? `, ${booking.name}` : ""}. We'll reach out within 24 hours to confirm details for your {booking?.service || "session"}.
                </p>
                <div className="flex items-center gap-2 text-xs text-[#52525B] mt-2">
                  <ShieldCheck size={14} /> Payments secured by PayPal
                </div>
              </div>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Booking;
